/**
 * Admin Invites Service
 * Handles API calls for admin invitation management
 *
 * All endpoints return StandardResponse<T> or PaginatedStandardResponse<T> formats
 */

import { apiClient } from '@/lib/api'
import type { StandardResponse, PaginatedStandardResponse, MessageResponse, PaginationInfo, ErrorResponse } from '@/types/api-response'
import { isErrorResponse, extractData, extractPaginatedData } from '@/types/api-response'
import type {
  AdminInvite,
  CreateInviteRequest,
  InviteListParams,
} from '@/types/invite'

export const adminInvitesService = {
  /**
   * Create a new admin invitation
   * @param data - Invite creation data
   * @returns Created admin invite
   * @throws Error if response is an error response
   */
  async createInvite(data: CreateInviteRequest): Promise<AdminInvite> {
    const response = await apiClient.post<StandardResponse<AdminInvite>>(
      '/api/v1/admin/invites',
      data
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractData(response.data)
  },

  /**
   * List admin invitations with optional filtering
   * @param params - Optional filter parameters
   * @returns Paginated list of invites
   * @throws Error if response is an error response
   */
  async listInvites(params?: InviteListParams): Promise<{ data: AdminInvite[]; pagination: PaginationInfo }> {
    const response = await apiClient.get<PaginatedStandardResponse<AdminInvite> | ErrorResponse>(
      '/api/v1/admin/invites',
      { params }
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractPaginatedData(response.data)
  },

  /**
   * Get a specific invitation by ID
   * @param inviteId - Invite ID
   * @returns Admin invite details
   * @throws Error if invite not found or response is error
   */
  async getInvite(inviteId: string): Promise<AdminInvite> {
    const response = await apiClient.get<StandardResponse<AdminInvite> | ErrorResponse>(
      `/api/v1/admin/invites/${inviteId}`
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractData(response.data)
  },

  /**
   * Revoke an admin invitation
   * @param inviteId - Invite ID to revoke
   * @throws Error if revocation fails
   */
  async revokeInvite(inviteId: string): Promise<void> {
    const response = await apiClient.delete<MessageResponse>(
      `/api/v1/admin/invites/${inviteId}`
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }
  },

  /**
   * Resend an admin invitation (creates new invite with same email/role)
   * @param inviteId - Invite ID to resend
   * @returns New admin invite
   * @throws Error if resend fails
   */
  async resendInvite(inviteId: string): Promise<AdminInvite> {
    const response = await apiClient.post<StandardResponse<AdminInvite>>(
      `/api/v1/admin/invites/${inviteId}/resend`
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractData(response.data)
  },
}
